import React, { useMemo, useState } from 'react';
import { FormControl, InputLabel, Select, MenuItem, Box, Chip, SelectChangeEvent, ListSubheader } from '@mui/material';
import { HomeExpenseGroupDto } from '../../types/homeExpenses';

interface HomeExpenseGroupSelectProps {
  groups: HomeExpenseGroupDto[];
  value: string;
  onChange: (publicId: string) => void;
  label?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  includeEmpty?: boolean;
  helperAdornment?: React.ReactNode;
}

/**
 * Generic select component for Home Expense Groups grouped by Category.
 * Ordering:
 *  1. Categories sorted by category.sortOrder then name.
 *  2. Groups inside each category sorted alphabetically by name.
 * If a group has no category it falls under an "Uncategorized" section at the end.
 */
export const HomeExpenseGroupSelect: React.FC<HomeExpenseGroupSelectProps> = ({
  groups,
  value,
  onChange,
  label = 'Grupo',
  size = 'small',
  fullWidth = true,
  disabled = false,
  required = false,
  includeEmpty = true,
  helperAdornment,
}) => {
  const [open, setOpen] = useState(false);

  const grouped = useMemo(() => {
    const map = new Map<string, { categoryName: string; sortOrder: number; color?: string; items: HomeExpenseGroupDto[] }>();
    const uncategorized: HomeExpenseGroupDto[] = [];

    groups.forEach(g => {
      if (g.category) {
        const key = g.category.publicId;
        if (!map.has(key)) {
          map.set(key, { categoryName: g.category.name, sortOrder: g.category.sortOrder, color: g.category.color, items: [] });
        }
        map.get(key)!.items.push(g);
      } else {
        uncategorized.push(g);
      }
    });

    const categories = Array.from(map.values())
      .sort((a,b) => a.sortOrder === b.sortOrder ? a.categoryName.localeCompare(b.categoryName) : a.sortOrder - b.sortOrder)
      .map(cat => ({
        ...cat,
        items: cat.items.sort((a,b) => a.name.localeCompare(b.name))
      }));

    const uncategorizedSorted = uncategorized.sort((a,b) => a.name.localeCompare(b.name));

    return { categories, uncategorized: uncategorizedSorted };
  }, [groups]);

  const handleChange = (e: SelectChangeEvent<string>) => {
    onChange(e.target.value as string);
    // Force close after selection (prevents rare cases where menu stays open)
    setOpen(false);
  };

  const handleItemSelect = (id: string) => {
    if (id !== value) {
      onChange(id);
    }
    setOpen(false);
  };

  return (
    <FormControl fullWidth={fullWidth} size={size} required={required} disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={value}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onChange={handleChange}
        renderValue={(selected) => {
        const grp = groups.find(g => g.publicId === selected);
        return grp ? grp.name : '';
      }}
        MenuProps={{ disableAutoFocusItem: false }}
      >
        {includeEmpty && <MenuItem value=""><em>Nenhum</em></MenuItem>}
        {grouped.categories.map(cat => (
          <React.Fragment key={cat.categoryName}>
            <ListSubheader disableSticky>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {cat.color && <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: cat.color }} />}
                {cat.categoryName}
              </Box>
            </ListSubheader>
            {cat.items.map(g => (
              <MenuItem key={g.publicId} value={g.publicId} onClick={() => handleItemSelect(g.publicId)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  {g.color && <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: g.color }} />}
                  <Box sx={{ flexGrow: 1 }}>{g.name}</Box>
                  <Chip
                    label={g.isIncomeGroup ? 'Receita' : 'Despesa'}
                    size="small"
                    color={g.isIncomeGroup ? 'success' : 'error'}
                    variant="outlined"
                    sx={{ pointerEvents: 'none' }}
                  />
                </Box>
              </MenuItem>
            ))}
          </React.Fragment>
        ))}
        {grouped.uncategorized.length > 0 && (
          <>
            <ListSubheader disableSticky>Sem categoria</ListSubheader>
            {grouped.uncategorized.map(g => (
              <MenuItem key={g.publicId} value={g.publicId} onClick={() => handleItemSelect(g.publicId)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  {g.color && <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: g.color }} />}
                  <Box sx={{ flexGrow: 1 }}>{g.name}</Box>
                  <Chip
                    label={g.isIncomeGroup ? 'Receita' : 'Despesa'}
                    size="small"
                    color={g.isIncomeGroup ? 'success' : 'error'}
                    variant="outlined"
                    sx={{ pointerEvents: 'none' }}
                  />
                </Box>
              </MenuItem>
            ))}
          </>
        )}
      </Select>
      {helperAdornment}
    </FormControl>
  );
};

export default HomeExpenseGroupSelect;
